import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AxiosError } from 'axios';

import { getCurrentUser } from '@/api/auth';
import { apiBaseUrl } from '@/api/base-url';
import type { CurrentUser } from '@/api/types';
import type { AuthError } from '@/auth/auth-errors';
import { loginWithPopup } from '@/auth/popup-login';
import { clearToken, storeToken } from '@/auth/token-storage';
import { paths } from '@/routes/paths';

export interface AuthContextProps {
  status: 'loading' | 'authenticated' | 'anonymous';
  isAuthenticated: boolean;
  user?: CurrentUser;
  displayName?: string;
  /** Sign-in is in flight: the popup is open, or the tab is navigating away. */
  isLoggingIn: boolean;
  /** Failure from the most recent popup sign-in, cleared when a new one starts. */
  loginError?: AuthError;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps>({
  status: 'loading',
  isAuthenticated: false,
  isLoggingIn: false,
  login: () => undefined,
  logout: () => undefined,
});

function displayNameFor(user: CurrentUser): string {
  return `${user.firstName} ${user.lastName}`.trim() || user.email;
}

const authorizeUrl = `${apiBaseUrl}/auth/microsoft`;

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'anonymous'>(
    'loading'
  );
  const [user, setUser] = useState<CurrentUser | undefined>(undefined);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<AuthError | undefined>(undefined);
  // Guards against a second popup on a double activation.
  const loginInFlight = useRef(false);

  useEffect(() => {
    // Object so TS does not treat the flag as always-false inside the promise callbacks.
    const active = { current: true };

    getCurrentUser()
      .then((current) => {
        if (!active.current) {
          return;
        }
        setUser(current);
        setStatus('authenticated');
      })
      .catch((error: unknown) => {
        if (!active.current) {
          return;
        }
        if (error instanceof AxiosError && error.response?.status === 401) {
          clearToken();
        }
        setUser(undefined);
        setStatus('anonymous');
      });

    return () => {
      active.current = false;
    };
  }, []);

  const login = useCallback((): void => {
    if (loginInFlight.current) {
      return;
    }
    loginInFlight.current = true;
    setIsLoggingIn(true);
    setLoginError(undefined);

    // Not awaited: `window.open` must stay in the click's own task.
    loginWithPopup(authorizeUrl)
      .then((result) => {
        if (result.status === 'blocked') {
          // Stay in the loading state: this tab is on its way out.
          window.location.assign(authorizeUrl);
          return;
        }

        if (result.status === 'success') {
          storeToken(result.token);
          // Full load, so the app re-bootstraps from the stored token.
          window.location.assign(paths.app);
          return;
        }

        // 'cancelled' falls through without an error.
        if (result.status === 'error') {
          setLoginError(result.reason);
        }
        loginInFlight.current = false;
        setIsLoggingIn(false);
      })
      .catch(() => {
        setLoginError('auth_failed');
        loginInFlight.current = false;
        setIsLoggingIn(false);
      });
  }, []);

  const logout = useCallback((): void => {
    clearToken();
    window.location.assign(paths.login);
  }, []);

  const value: AuthContextProps = {
    status,
    isAuthenticated: status === 'authenticated',
    user,
    displayName: user ? displayNameFor(user) : undefined,
    isLoggingIn,
    loginError,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
